import {
  Card,
  Checkbox,
  Grid,
  GridBreak,
  Link,
  pn,
  Stack,
  Typography,
  useElementBreakpoints,
  ValidationErrors,
} from "@compass/core";
import { Select } from "components";
import { Export, ExportResource } from "generated/openApi";
import produce from "immer";
import { useState } from "react";

export const ExportType = (props: {
  _export: Export;
  setExport: (value: Export) => void;
  resource: ExportResource;
  validationErrors: ValidationErrors;
}) => {
  const { breakpointsRef, setBreakpoints, smallFieldBreakpoints } = useElementBreakpoints();
  const [showMore, setShowMore] = useState(false);
  const subTypes = props.resource.exportSubTypes?.filter((x) => x.exportTypeId === props._export?.exportTypeId);

  const canDisplayFiltersForType =
    props._export?.exportTypeId && props.resource.displayFilterExportTypeIds?.includes(props._export?.exportTypeId);

  const canDisplayFiltersForSubType =
    props._export?.exportSubTypeId &&
    props.resource.displayFilterExportSubTypeIds?.includes(props._export?.exportSubTypeId);

  return (
    <Card>
      <Grid ref={breakpointsRef} container spacing="sm">
        <Grid item xs={smallFieldBreakpoints}>
          <Select
            label="Type"
            required
            value={props.resource.exportTypes?.find((x) => x.id === props._export?.exportTypeId)}
            options={props.resource.exportTypes}
            getOptionLabel={(option) => option.name}
            onChange={(e, value) =>
              props.setExport(
                produce(props._export, (draft) => {
                  draft.exportTypeId = value?.id;
                  draft.exportSubTypeId = undefined;
                })
              )
            }
            errors={props.validationErrors[pn<Export>("exportTypeId")]}
          />

          {props._export?.exportTypeId && (
            <Checkbox
              label={`${canDisplayFiltersForType ? "Show filters in result" : "Show filters not available"} `}
              checked={props._export?.filterDisplayForExportTypeId}
              onChange={(e) =>
                props.setExport(
                  produce(props._export, (draft) => {
                    draft.filterDisplayForExportTypeId = e.target.checked;
                  })
                )
              }
              disabled={!canDisplayFiltersForType}
            />
          )}
        </Grid>

        {subTypes && subTypes?.length > 0 && (
          <Grid item xs={smallFieldBreakpoints}>
            <Select
              label="Sub-Type"
              value={subTypes?.find((x) => x.id === props._export?.exportSubTypeId)}
              options={subTypes}
              getOptionLabel={(option) => option.name}
              onChange={(e, value) =>
                props.setExport(
                  produce(props._export, (draft) => {
                    draft.exportSubTypeId = value?.id;
                  })
                )
              }
              errors={props.validationErrors[pn<Export>("exportSubTypeId")]}
            />

            {props._export?.exportSubTypeId && (
              <Checkbox
                label={`${canDisplayFiltersForSubType ? "Show filters in result" : "Show filters not available"} `}
                checked={props._export?.filterDisplayForExportSubTypeId}
                onChange={(e) =>
                  props.setExport(
                    produce(props._export, (draft) => {
                      draft.filterDisplayForExportSubTypeId = e.target.checked;
                    })
                  )
                }
                disabled={!canDisplayFiltersForSubType}
              />
            )}
          </Grid>
        )}

        {!showMore ? (
          <Grid item style={{ flexGrow: 1, flexBasis: 0 }}>
            <Stack direction="row" alignItems="center" justifyContent={"flex-end"} height="100%" width="100%">
              <Link underline="hover" onClick={() => setShowMore(!showMore)}>
                <Typography variant="subtitle1">More</Typography>
              </Link>
            </Stack>
          </Grid>
        ) : (
          <>
            <Grid item xs={smallFieldBreakpoints}>
              <Select
                required
                label="Timezone"
                value={props.resource.timezones?.find((x) => x.item1 === props._export?.timezone)}
                options={props.resource.timezones}
                getOptionLabel={(option) => option.item2!}
                onChange={(e, value) =>
                  props.setExport(
                    produce(props._export, (draft) => {
                      draft.timezone = value ? value.item1 : undefined;
                    })
                  )
                }
                errors={props.validationErrors[pn<Export>("timezone")]}
              />
            </Grid>

            <Grid item style={{ flexGrow: 1, flexBasis: 0 }}>
              <Stack direction="row" alignItems="center" justifyContent={"flex-end"} height="100%" width="100%">
                <Link underline="hover" onClick={() => setShowMore(!showMore)}>
                  <Typography variant="subtitle1">Less</Typography>
                </Link>
              </Stack>
            </Grid>
          </>
        )}
      </Grid>
    </Card>
  );
};
